import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

const getBulkDeleteCategoryErrorMessage = (error, { hasChildren = false } = {}) => {
  if (hasChildren) {
    return "Some selected categories can’t be deleted because they have sub-categories. Delete/move the sub-categories first.";
  }

  const status = error?.response?.status;
  const data = error?.response?.data;

  const rawMessage =
    (typeof data === "string" && data) ||
    data?.message ||
    data?.error ||
    data?.detail ||
    error?.message;

  const normalized = String(rawMessage || "").toLowerCase();

  const looksLikeHasChildren =
    normalized.includes("sub") ||
    normalized.includes("child") ||
    normalized.includes("children") ||
    normalized.includes("linked") ||
    normalized.includes("dependent") ||
    normalized.includes("constraint") ||
    normalized.includes("foreign key");

  if (status === 409 || (status === 500 && looksLikeHasChildren) || looksLikeHasChildren) {
    return "Some selected categories can’t be deleted because they have sub-categories or are linked to other items.";
  }

  if (status === 403) {
    return "You don’t have permission to delete these categories.";
  }

  if (rawMessage) return String(rawMessage);

  return "Failed to delete categories. Please try again.";
};

export default function DeleteCategoriesBulkDialog({
  open,
  onOpenChange,
  categoryIds,
  categories,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  const selectedIdSet = useMemo(
    () => new Set((Array.isArray(categoryIds) ? categoryIds : []).map((id) => String(id))),
    [categoryIds]
  );

  const selectedCategories = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return list.filter((cat) => selectedIdSet.has(String(cat?.id)));
  }, [categories, selectedIdSet]);

  const hasChildren = useMemo(() => {
    if (selectedCategories.length === 0) return false;
    const list = Array.isArray(categories) ? categories : [];
    const selectedIds = new Set(selectedCategories.map((c) => c?.id));

    return list.some((cat) => {
      const parentId = cat?.parent_category_id ?? null;
      return parentId != null && selectedIds.has(parentId);
    });
  }, [categories, selectedCategories]);

  const title = "Delete Categories";
  const count = selectedIdSet.size;

  const handleDelete = async () => {
    if (count === 0) return;

    if (hasChildren) {
      toast.error(
        "Some selected categories can’t be deleted because they have sub-categories. Delete/move the sub-categories first."
      );
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          category_ids: Array.from(selectedIdSet).map((id) => {
            const maybeNum = Number(id);
            return Number.isFinite(maybeNum) ? maybeNum : id;
          }),
        },
      };

      const res = await axios.delete(
        api_endpoint + "/course/category/delete/bulk",
        reqConfig
      );

      const deletedCount =
        Number(res?.data?.deleted_count ?? res?.data?.deletedCount) || count;

      toast.success(`Deleted ${deletedCount} categor${deletedCount === 1 ? "y" : "ies"}`);
      onOpenChange?.(false);
      if (onDeleted) onDeleted(res?.data);
    } catch (error) {
      toast.error(getBulkDeleteCategoryErrorMessage(error, { hasChildren }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="font-body text-(--neutral-900)">
          Are you sure you want to delete <b>{count}</b> categor{count === 1 ? "y" : "ies"}?
          This action cannot be undone.
        </div>

        {hasChildren ? (
          <div className="mt-3 rounded-md bg-(--neutral-100) p-3 text-sm text-(--neutral-900)">
            Some selected categories have sub-categories. Delete/move the sub-categories first, then try again.
          </div>
        ) : null}

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={loading}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={loading || hasChildren || count === 0}
            className=""
          >
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
